// Minimal Matrix Client-Server (v3) client over fetch — no SDK, no crypto.
// Rooms + timelines live in an in-memory store; screens subscribe with
// useSyncExternalStore. A single long-poll /sync loop keeps everything live:
// messages, typing (m.typing), read receipts (m.receipt), reactions (m.reaction),
// invites, room tags (hide/archive), Spaces (workspaces) and an optional
// per-room agent-info state event. Hermes approval gates ("!approve / !deny")
// are detected so the UI can answer them with a button. Encrypted rooms are
// recognised and shown as a placeholder (not decrypted).
import { useSyncExternalStore } from 'react';
import { AppState, Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MATRIX_HS, INVITE_API, INVITE_BOT } from './config';

// ── Session ──
export type Session = { accessToken: string; userId: string; deviceId: string };
let session: Session | null = null;
export const setSession = (s: Session | null) => { session = s; };
export const getSession = () => session;
const serverName = () => (session?.userId.split(':')[1] || MATRIX_HS.replace(/^https?:\/\//, ''));

export class MatrixError extends Error {
  status: number; errcode?: string; data?: any;
  constructor(msg: string, status: number, errcode?: string, data?: any) { super(msg); this.status = status; this.errcode = errcode; this.data = data; }
}

async function req(path: string, opts: RequestInit = {}, useAuth = true): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...((opts.headers as any) || {}) };
  if (useAuth && session) headers.Authorization = `Bearer ${session.accessToken}`;
  const resp = await fetch(MATRIX_HS + path, { ...opts, headers });
  const text = await resp.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON body */ }
  if (!resp.ok) throw new MatrixError(data.error || `${resp.status} ${resp.statusText}`, resp.status, data.errcode, data);
  return data;
}

const txnId = (p: string) => `${p}${Date.now()}.${Math.random().toString(36).slice(2, 8)}`;
const roomPath = (roomId: string) => `/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}`;
const enc = encodeURIComponent;

/** "name", "@name:server" or "name:server" → full Matrix ID on our server. */
export function toMxid(input: string): string {
  const raw = input.trim();
  if (/^@[^:]+:.+$/.test(raw)) return raw;
  const m = /^@?([^:@\s]+)(?::(.+))?$/.exec(raw);
  if (!m) return raw;
  return `@${m[1]}:${m[2] || serverName()}`;
}

// ── Auth ──
export async function login(user: string, password: string): Promise<Session> {
  // Accept "name", "@name:server", or "name:server" — always send the bare localpart,
  // so a missing/extra "@" or server suffix can't turn into a "user does not exist".
  const raw = user.trim();
  const m = /^@?([^:@\s]+)(?::.*)?$/.exec(raw);
  const localpart = m ? m[1] : raw;
  const d = await req('/_matrix/client/v3/login', {
    method: 'POST',
    body: JSON.stringify({
      type: 'm.login.password',
      identifier: { type: 'm.id.user', user: localpart },
      password,
      initial_device_display_name: 'Helm Mobile',
    }),
  }, false);
  return { accessToken: d.access_token, userId: d.user_id, deviceId: d.device_id };
}
// ── Email-code sign-in (the helm_email_login module on our homeserver) ──
export const EMAIL_LOGIN_TYPE = 'ai.getfxm.email_code';
/** Ask the homeserver to email a sign-in code. It answers the same whether or not the address has an account. */
export async function requestEmailCode(email: string): Promise<void> {
  await req('/_synapse/client/helm/email_code/request', { method: 'POST', body: JSON.stringify({ email: email.trim().toLowerCase() }) }, false);
}
export async function loginWithEmailCode(email: string, code: string): Promise<Session> {
  const addr = email.trim().toLowerCase();
  const d = await req('/_matrix/client/v3/login', {
    method: 'POST',
    body: JSON.stringify({
      type: EMAIL_LOGIN_TYPE,
      identifier: { type: 'm.id.user', user: addr },
      email: addr,
      code: code.replace(/\D/g, ''),
      initial_device_display_name: 'Helm Mobile',
    }),
  }, false);
  return { accessToken: d.access_token, userId: d.user_id, deviceId: d.device_id };
}
export const whoami = (): Promise<{ user_id: string }> => req('/_matrix/client/v3/account/whoami');
export const serverLogout = () => req('/_matrix/client/v3/logout', { method: 'POST', body: '{}' }).catch(() => {});

// ── Account ──
export async function getDisplayName(): Promise<string> {
  if (!session) return '';
  const d = await req(`/_matrix/client/v3/profile/${enc(session.userId)}/displayname`).catch(() => ({}));
  return d?.displayname || '';
}
export async function setDisplayName(name: string): Promise<void> {
  if (!session) throw new Error('Not signed in');
  await req(`/_matrix/client/v3/profile/${enc(session.userId)}/displayname`, { method: 'PUT', body: JSON.stringify({ displayname: name.trim() }) });
}
/** Change my password (Matrix user-interactive auth with the current password). Other devices stay signed in. */
export async function changePassword(current: string, next: string): Promise<void> {
  if (!session) throw new Error('Not signed in');
  const auth: any = { type: 'm.login.password', identifier: { type: 'm.id.user', user: session.userId }, password: current };
  const body = (a: any) => JSON.stringify({ new_password: next, logout_devices: false, auth: a });
  try {
    await req('/_matrix/client/v3/account/password', { method: 'POST', body: body(auth) });
  } catch (e: any) {
    // Some servers insist on a UIA session: take the one from the 401 and retry once.
    if (e?.status === 401 && e?.data?.session && !e?.data?.errcode) {
      await req('/_matrix/client/v3/account/password', { method: 'POST', body: body({ ...auth, session: e.data.session }) });
      return;
    }
    if (e?.status === 401 || e?.errcode === 'M_FORBIDDEN') throw new MatrixError('Current password is wrong.', 401, 'M_FORBIDDEN');
    throw e;
  }
}
/** Throw away the local snapshot and re-download everything from the server. */
export async function resync(): Promise<void> {
  running = false; abort?.abort();
  resetStore(); await clearCache(); syncState = 'idle'; notify();
  startSync();
}

// ── Model ──
export type MEvent = {
  id: string; roomId: string; sender: string; ts: number;
  kind: 'text' | 'image' | 'file' | 'encrypted' | 'system';   // system = "X joined/left" lines
  body: string; html?: string; mxc?: string; info?: any;
  localUri?: string;   // our own just-sent attachment: show the local file until the server copy arrives
  replyTo?: string;    // event id this message replies to (m.in_reply_to)
  threadRoot?: string; // this message lives in the thread rooted at that event (m.thread)
  threadCount?: number;     // on a root: server-reported reply count
  threadLatestTs?: number;  // on a root: time of the latest reply
  threadLatestBy?: string;  // on a root: who wrote the latest reply (the server says so without sending the replies)
  card?: any;          // structured "Helm card" payload (ai.getfxm.card or a ```json block)
  decision?: Decision; // a person's decision on an approval card (content key ai.getfxm.decision)
  thread?: { kind: string; title?: string }; // root of a thread Helm/agents opened on purpose (content key ai.getfxm.thread)
  pending?: boolean;   // local echo, send in flight
  failed?: boolean;    // send failed — kept in the thread so the user can retry
};
export type AgentInfo = { model?: string; context_used?: number; context_max?: number; [k: string]: any };
export type Room = {
  id: string; name: string; topic?: string; encrypted: boolean; isDm: boolean;
  members: Record<string, string>; heroes: string[]; joined: number;
  last?: MEvent; unread: number; prevBatch?: string; nameState?: string; alias?: string;
  typing: string[];                 // user ids currently typing (excluding me)
  reads: Record<string, string>;    // user id → latest event id they've read
  threadUnread: Record<string, number>; // thread root → unread replies (server-counted, per-thread receipts)
  tags: Record<string, any>;        // m.tag → e.g. { 'm.lowpriority': {...} } = hidden/archived
  isSpace: boolean;                 // m.room.create type m.space → a workspace
  children: string[];               // for spaces: child room ids (m.space.child)
  parents: string[];                // m.space.parent state keys
  agentInfo?: AgentInfo;            // optional `ai.getfxm.agent` state event published by the agent
  membersLoaded?: boolean;
  joinRule?: string;                // 'invite' (private) | 'restricted' (workspace members can join) | 'public'
  power?: any;                      // m.room.power_levels content
  avatarMxc?: string;               // m.room.avatar
  memberAvatars: Record<string, string>; // user id → avatar mxc
};

/** Can I set this state event type in the room? (Matrix power levels.) */
export function canSetState(room: Room | undefined, eventType: string): boolean {
  if (!room || !session) return false;
  const p = room.power || {};
  const mine = p.users?.[session.userId] ?? p.users_default ?? 0;
  const need = p.events?.[eventType] ?? p.state_default ?? 50;
  return mine >= need;
}
export type Invite = { id: string; name: string; inviter: string; inviterName: string; isDm: boolean; isSpace: boolean };

export const localpart = (uid: string) => uid.replace(/^@/, '').split(':')[0];
export const senderName = (room: Room | undefined, uid: string) => room?.members[uid] || localpart(uid);
export const HIDDEN_TAG = 'm.lowpriority';
export const isHidden = (r: Room) => !!r.tags[HIDDEN_TAG];
/** m.favourite = "keep this room in the list" — it is never auto-archived for being idle. */
export const KEEP_TAG = 'm.favourite';
export const isKept = (r: Room) => !!r.tags[KEEP_TAG];
/** "parifma — FxM Agent" → "parifma". The suffix is provisioning boilerplate, not a name. */
export const cleanName = (n: string) => n.replace(/\s*[—–-]\s*FxM Agent\s*$/i, '').trim() || n;
/** Can I invite people to this room / workspace? */
export function canInvite(room: Room | undefined): boolean {
  if (!room || !session) return false;
  const p = room.power || {};
  return (p.users?.[session.userId] ?? p.users_default ?? 0) >= (p.invite ?? 0);
}
/** Can I remove people from this room / workspace? */
export function canKick(room: Room | undefined): boolean {
  if (!room || !session) return false;
  const p = room.power || {};
  return (p.users?.[session.userId] ?? p.users_default ?? 0) >= (p.kick ?? 50);
}
/** Slack's "only admins can invite": the workspace's invite power level. 50 is admins and moderators, 0 is everyone. */
export const ADMIN_LEVEL = 50;
export const adminsOnlyInvite = (room: Room | undefined) => (room?.power?.invite ?? 0) >= ADMIN_LEVEL;
/** The member with the highest power level — who "runs" this room or workspace. */
export function roomAdmin(room: Room | undefined): string | undefined {
  const users: Record<string, number> = room?.power?.users || {};
  return Object.entries(users).sort((a, b) => b[1] - a[1])[0]?.[0];
}

// ── Store ──
const rooms = new Map<string, Room>();
const invites = new Map<string, Invite>();
const timelines = new Map<string, MEvent[]>();
const seen = new Map<string, Set<string>>();
const reactions = new Map<string, Map<string, Set<string>>>(); // eventId → key → senders
const answered = new Map<string, GateChoice>();                   // requests answered from this device this session, and how
let directRooms = new Set<string>();
let directMap: Record<string, string[]> = {};                    // m.direct content (user → room ids)
const EMPTY: MEvent[] = [];
let version = 0;
const listeners = new Set<() => void>();
function notify() { version++; listeners.forEach((l) => l()); scheduleSave(); }

// ── Local cache: rooms + recent timelines + sync position, so the app opens instantly
// and the next /sync is incremental instead of a full re-download. ──
const CACHE_KEY = 'mx_cache_v1';
const CACHE_EVENTS = 100;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave() {
  if (!session || !nextBatch) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { saveCache(); }, 1500);
}
async function saveCache() {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  if (!session || !nextBatch) return;
  try {
    const tl: Record<string, MEvent[]> = {};
    const truncated = new Set<string>();
    for (const [id, evs] of timelines) {
      const real = evs.filter((e) => !e.pending && !e.failed);
      if (real.length > CACHE_EVENTS) truncated.add(id);
      tl[id] = real.slice(-CACHE_EVENTS);
    }
    const rx: Record<string, Record<string, string[]>> = {};
    for (const [eid, m] of reactions) { const o: Record<string, string[]> = {}; m.forEach((set, k) => { o[k] = [...set]; }); rx[eid] = o; }
    const data = {
      v: 1, user: session.userId, nextBatch, directMap, reactionIds: Object.fromEntries(reactionIds),
      // "Load earlier" needs an unbroken history; if we dropped events, drop the token too.
      rooms: [...rooms.values()].map((r) => ({ ...r, typing: [], membersLoaded: false, prevBatch: truncated.has(r.id) ? undefined : r.prevBatch })),
      invites: [...invites.values()], timelines: tl, reactions: rx,
      // Thread lists, so a room shows its thread count at once after a reload. Only what the lists draw.
      threads: Object.fromEntries([...threadRoots].map(([id, evs]) => [id, evs.slice(0, 50).map((e) => ({
        id: e.id, roomId: e.roomId, sender: e.sender, ts: e.ts, kind: e.kind, body: e.body.slice(0, 300), thread: e.thread,
        threadCount: e.threadCount, threadLatestTs: e.threadLatestTs, threadLatestBy: e.threadLatestBy, card: e.card ? { type: e.card.type, title: e.card.title } : undefined,
      }))])),
    };
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch { /* cache is best-effort */ }
}
async function loadCache(): Promise<boolean> {
  try {
    const raw = await AsyncStorage.getItem(CACHE_KEY); if (!raw) return false;
    const d = JSON.parse(raw);
    if (d?.v !== 1 || d.user !== session?.userId || !d.nextBatch) return false;
    for (const r of d.rooms || []) { const room: Room = { ...newRoom(r.id), ...r }; room.name = resolveName(room); rooms.set(r.id, room); }
    for (const i of d.invites || []) invites.set(i.id, i);
    directMap = d.directMap || {}; directRooms = new Set((Object.values(directMap) as string[][]).flat());
    for (const [id, evs] of Object.entries<MEvent[]>(d.threads || {})) threadRoots.set(id, evs);
    for (const [id, evs] of Object.entries<MEvent[]>(d.timelines || {})) {
      // Older caches kept the raw text of hand-typed cards (the local echo was never re-parsed); detect them again.
      const fixed = evs.map((e) => (e.kind === 'text' && !e.card && e.body.includes('{') ? { ...e, ...parseCard({}, e.body) } : e));
      timelines.set(id, fixed); seen.set(id, new Set(fixed.map((e) => e.id)));
    }
    for (const [eid, o] of Object.entries<Record<string, string[]>>(d.reactions || {})) {
      const m = new Map<string, Set<string>>(); for (const [k, who] of Object.entries(o)) m.set(k, new Set(who)); reactions.set(eid, m);
    }
    for (const [k, eid] of Object.entries<string>(d.reactionIds || {})) {
      reactionIds.set(k, eid); const [target, key, sender] = k.split(SEP); reactionByEventId.set(eid, { target, key, sender });
    }
    nextBatch = d.nextBatch;
    return true;
  } catch { return false; }
}
function resetStore() {
  rooms.clear(); invites.clear(); timelines.clear(); seen.clear(); reactions.clear(); answered.clear();
  reactionIds.clear(); reactionByEventId.clear(); threadFetched.clear(); threadRoots.clear(); threadRootsAt.clear(); agentUsers.clear();
  directRooms = new Set(); directMap = {}; nextBatch = null;
}
export const clearCache = () => AsyncStorage.removeItem(CACHE_KEY).catch(() => {});
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

function cached<T>(compute: () => T) {
  let v = -1; let value: T;
  return () => { if (v !== version) { value = compute(); v = version; } return value; };
}

const roomListSnap = cached(() => [...rooms.values()].sort((a, b) => (b.last?.ts || 0) - (a.last?.ts || 0)));
export function useRooms(): Room[] { return useSyncExternalStore(subscribe, roomListSnap); }
export function useRoom(roomId: string): Room | undefined { return useSyncExternalStore(subscribe, () => rooms.get(roomId)); }
export function useTimeline(roomId: string): MEvent[] { return useSyncExternalStore(subscribe, () => timelines.get(roomId) || EMPTY); }
const inviteSnap = cached(() => [...invites.values()]);
export function useInvites(): Invite[] { return useSyncExternalStore(subscribe, inviteSnap); }

export type SyncState = 'idle' | 'initial' | 'live' | 'error';
let syncState: SyncState = 'idle';
export function useSyncState(): SyncState { return useSyncExternalStore(subscribe, () => syncState); }
/** Re-render on any store change (reactions live outside the timeline arrays). */
export function useStoreVersion(): number { return useSyncExternalStore(subscribe, () => version); }

/** Users who published `ai.getfxm.agent` (state or timeline) — the strongest signal that a member is a bot. */
const agentUsers = new Set<string>();
/** Agents we know about. A provisioned room is named "<agent> — FxM Agent"; the bot is the member carrying
 *  that name (display name or localpart), else the only other member. Everyone else in the room is a
 *  human — even inside an agent room. Anyone who published `ai.getfxm.agent` always counts. */
export type KnownAgent = { id: string; name: string };
const agentsSnap = cached(() => {
  const me = session?.userId; const out = new Map<string, string>();
  const norm = (s: string) => s.trim().toLowerCase();
  for (const r of rooms.values()) {
    for (const [uid, name] of Object.entries(r.members)) if (uid !== me && agentUsers.has(uid)) out.set(uid, name);
    const m = /^(.*?)\s*[—–-]\s*FxM Agent\s*$/i.exec(r.nameState || r.name);
    if (!m) continue;
    const want = norm(m[1]);
    const others = Object.entries(r.members).filter(([uid]) => uid !== me);
    const hit = others.find(([uid, name]) => norm(name) === want || norm(localpart(uid)) === want)
      || others.find(([uid, name]) => want.length >= 3 && [norm(name), norm(localpart(uid))].some((n) => n.length >= 3 && (want.includes(n) || n.includes(want))))
      || (others.length === 1 ? others[0] : undefined);
    if (hit) out.set(hit[0], hit[1]);
  }
  for (const uid of agentUsers) if (uid !== me && !out.has(uid)) out.set(uid, localpart(uid));
  return [...out.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
});
export function useKnownAgents(): KnownAgent[] { return useSyncExternalStore(subscribe, agentsSnap); }
const agentIdsSnap = cached(() => new Set(agentsSnap().map((a) => a.id)));
/** Is this member a bot? (Known agents = the non-me members of provisioned agent rooms.) */
export const isAgentUser = (uid: string) => agentIdsSnap().has(uid);
/** The platform's invite bot: plumbing for email invites, not a member anyone should see or be named after. */
export const isServiceUser = (uid: string) => uid === INVITE_BOT;
export function useIsAgent(): (uid: string) => boolean { const set = useSyncExternalStore(subscribe, agentIdsSnap); return (uid) => set.has(uid); }

/** Home grouping: spaces (workspaces) with their rooms, loose rooms, and archived. */
export type RoomGroups = { spaces: { space: Room; rooms: Room[] }[]; loose: Room[]; archived: Room[] };
const DAY = 24 * 3600 * 1000;
export function partitionRooms(all: Room[], archiveDays = 7): RoomGroups {
  const now = Date.now();
  const WEEK = archiveDays > 0 ? archiveDays * DAY : Number.POSITIVE_INFINITY;
  const spaces = all.filter((r) => r.isSpace);
  const inSpace = new Set<string>();
  const groups = spaces.map((space) => {
    const rs = space.children.map((id) => rooms.get(id)).filter((r): r is Room => !!r && !r.isSpace);
    rs.forEach((r) => inSpace.add(r.id));
    return { space, rooms: rs.filter((r) => !isHidden(r)) };
  });
  const loose: Room[] = []; const archived: Room[] = [];
  for (const r of all) {
    if (r.isSpace) continue;
    const idle = !!r.last && now - r.last.ts > WEEK && !isKept(r);
    if (isHidden(r) || (idle && !inSpace.has(r.id))) { archived.push(r); continue; }
    if (!inSpace.has(r.id)) loose.push(r);
  }
  return { spaces: groups, loose, archived };
}

/** The workspaces a room sits in, among those I am a member of: the room's m.space.parent, and the spaces whose
 *  m.space.child lists it. `all` is the current room list when a screen already holds it. */
export function workspacesOf(roomId: string, all: Room[] = [...rooms.values()]): Room[] {
  const r = all.find((x) => x.id === roomId);
  const ids = new Set<string>(r?.parents || []);
  for (const sp of all) if (sp.isSpace && sp.children.includes(roomId)) ids.add(sp.id);
  return all.filter((sp) => sp.isSpace && ids.has(sp.id));
}

// ── Reactions ──
export function getReactions(eventId: string): Record<string, string[]> {
  const m = reactions.get(eventId); if (!m) return {};
  const out: Record<string, string[]> = {};
  m.forEach((set, key) => { out[key] = [...set]; });
  return out;
}
const SEP = String.fromCharCode(1); // separator for reaction lookup keys (never appears in ids/emoji)
const reactionIds = new Map<string, string>();                                        // target|key|sender → reaction event id
const reactionByEventId = new Map<string, { target: string; key: string; sender: string }>();
function recordReaction(ev: any) {
  const rel = ev?.content?.['m.relates_to'];
  if (ev?.type !== 'm.reaction' || !rel || rel.rel_type !== 'm.annotation' || !rel.event_id || !rel.key) return;
  let m = reactions.get(rel.event_id); if (!m) { m = new Map(); reactions.set(rel.event_id, m); }
  let s = m.get(rel.key); if (!s) { s = new Set(); m.set(rel.key, s); }
  s.add(ev.sender);
  if (ev.event_id) {
    reactionIds.set([rel.event_id, rel.key, ev.sender].join(SEP), ev.event_id);
    reactionByEventId.set(ev.event_id, { target: rel.event_id, key: rel.key, sender: ev.sender });
  }
}
/** A redaction removes a reaction (or a message) we know about. */
function applyRedaction(roomId: string, redactedId: string) {
  const r = reactionByEventId.get(redactedId);
  if (r) {
    const m = reactions.get(r.target); const s = m?.get(r.key);
    if (s) { s.delete(r.sender); if (!s.size) m!.delete(r.key); }
    reactionIds.delete([r.target, r.key, r.sender].join(SEP)); reactionByEventId.delete(redactedId);
    return;
  }
  const tl = timelines.get(roomId);
  if (tl?.some((e) => e.id === redactedId)) timelines.set(roomId, tl.filter((e) => e.id !== redactedId));
  for (const [k, list] of threadFetched) if (k.startsWith(roomId + '|') && list.some((e) => e.id === redactedId)) threadFetched.set(k, list.filter((e) => e.id !== redactedId));
  const roots = threadRoots.get(roomId);
  if (roots?.some((e) => e.id === redactedId)) threadRoots.set(roomId, roots.filter((e) => e.id !== redactedId));
}
export const myReactionId = (target: string, key: string) =>
  session ? reactionIds.get([target, key, session.userId].join(SEP)) : undefined;

// ── Approvals ──
// Two kinds of request wait for a person, and both get the same buttons:
//
// 1. A Hermes gate. Hermes posts a prompt like "`!approve` … or `!deny` … click the reaction: ✅ / ❌" and
//    waits for the text command or a reaction on that message. These are technical ("may I run this?").
// 2. An approval card. An agent's approvals skill attaches a card of type "approval" to a message
//    (helm-infra docs/approval-cards.md): the thing to approve, in full, before it goes out. These are
//    the functional approvals. Several can wait in one room at once, so a card is answered by a reply
//    to THAT card, never by "I wrote something later".
const OPEN_STATUS = new Set(['', 'pending', 'waiting', 'open', 'requested']);
/** Does a card with this status still wait for a decision? */
export const waits = (status: unknown) => OPEN_STATUS.has(String(status ?? '').toLowerCase());
export function isApproval(ev: MEvent): boolean {
  const c = ev.card;
  // Not the Atlas / Ann / Prometheus hand-off envelopes: they are drawn as cards, but the agents behind them
  // do not read a decision, so they stay display only.
  return !!c && !c.envelope && ev.sender !== session?.userId && String(c.type || '').toLowerCase() === 'approval'
    && waits(c.status);
}
const isTextGate = (ev: MEvent) =>
  ev.kind === 'text' && ev.sender !== session?.userId && /!approve\b/i.test(ev.body) && /!deny\b/i.test(ev.body);
export function isGate(ev: MEvent): boolean { return isApproval(ev) || isTextGate(ev); }
export type Gate = { room: Room; event: MEvent };
const norm = (k: string) => k.split(String.fromCharCode(0xfe0f)).join(''); // strip emoji variation selector
const APPROVE_KEYS = ['✅', '✔', '☑', '👍'].map(norm);
const DENY_KEYS = ['❌', '❎', '✖', '👎', '🚫'].map(norm);
const ALWAYS_KEYS = ['♾', '∞'].map(norm);

/** Does this later event settle that card? A person's decision on it, or the agent's newer copy of it. */
function settles(card: MEvent, later: MEvent): boolean {
  const id = card.card?.id;
  if (later.sender === card.sender) return !!id && later.card?.id === id;            // the agent re-issued or closed it
  if (later.decision) return later.decision.card_event === card.id || (!!id && later.decision.id === id);
  return later.replyTo === card.id;
}
function gateOpen(ev: MEvent, tl: MEvent[], idx: number): boolean {
  const me = session?.userId;
  if (answered.has(ev.id)) return false;
  const rx = reactions.get(ev.id);
  if (rx && me) for (const set of rx.values()) if (set.has(me)) return false;     // I reacted on it
  if (isApproval(ev)) {
    for (let j = idx + 1; j < tl.length; j++) if (settles(ev, tl[j])) return false;
    return true;
  }
  for (let j = idx + 1; j < tl.length; j++) if (tl[j].sender === me) return false; // I wrote something after it
  return true;
}
export function isGateOpen(roomId: string, ev: MEvent): boolean {
  const tl = timelines.get(roomId) || [];
  const idx = tl.findIndex((e) => e.id === ev.id);
  return idx >= 0 && gateOpen(ev, tl, idx);
}
export const isAnsweredHere = (eventId: string) => answered.has(eventId);
/** What was decided on a request, when we can tell: from this device, or from a decision in the room. */
export function decisionOn(ev: MEvent, list?: MEvent[]): GateChoice | undefined {
  const here = answered.get(ev.id);
  if (here) return here;
  const tl = list || timelines.get(ev.roomId) || [];
  const at = tl.findIndex((e) => e.id === ev.id);
  const id = ev.card?.id;
  if (!ev.card) {
    // A Hermes gate answered from elsewhere: my reaction on it, or the command I typed right after it. Anything
    // else I wrote after it is no answer: Hermes drops the request then, and the row says so.
    const me = session?.userId; const rx = reactions.get(ev.id);
    if (rx && me) for (const [key, set] of rx) if (set.has(me)) { const k = norm(key); if (ALWAYS_KEYS.includes(k)) return 'always'; if (APPROVE_KEYS.includes(k)) return 'approve'; if (DENY_KEYS.includes(k)) return 'deny'; }
    for (let j = at + 1; at >= 0 && j < tl.length; j++) {
      if (tl[j].sender !== me || tl[j].kind !== 'text') continue;
      const t = tl[j].body.trim().toLowerCase();
      return /^!approve\s+(always|session)\b/.test(t) ? 'always' : /^!approve\b/.test(t) ? 'approve' : /^!deny\b/.test(t) ? 'deny' : undefined;
    }
    return undefined;
  }
  // The first decision after the request. A newer copy of the same card ends the search: what follows is about that one.
  for (let j = at + 1; at >= 0 && j < tl.length; j++) {
    const d = tl[j].decision;
    if (d && (d.card_event === ev.id || (!!id && d.id === id))) return d.decision;
    if (id && tl[j].sender === ev.sender && tl[j].card?.id === id) return undefined;
  }
  return undefined;
}
/** Like isGateOpen, but judged within an arbitrary list (e.g. a thread's fetched replies). */
export function isGateOpenAmong(ev: MEvent, list: MEvent[]): boolean {
  const idx = list.findIndex((e) => e.id === ev.id);
  return idx >= 0 && gateOpen(ev, list, idx);
}

const gatesSnap = cached(() => {
  const out: Gate[] = [];
  for (const room of rooms.values()) {
    const tl = timelines.get(room.id); if (!tl?.length) continue;
    // Every approval card that still waits, and the latest Hermes gate (only the latest one can be open).
    for (let i = tl.length - 1, gateSeen = false; i >= 0; i--) {
      if (isApproval(tl[i])) { if (gateOpen(tl[i], tl, i)) out.push({ room, event: tl[i] }); continue; }
      if (gateSeen || !isTextGate(tl[i])) continue;
      gateSeen = true;
      if (gateOpen(tl[i], tl, i)) out.push({ room, event: tl[i] });
    }
  }
  return out.sort((a, b) => b.event.ts - a.event.ts);
});
/** Open approvals across all rooms, newest first — the "Needs you" list. */
export function useOpenGates(): Gate[] { return useSyncExternalStore(subscribe, gatesSnap); }

/** The line under a Hermes gate nobody answered before writing on: Hermes dropped it, and the buttons went with it. */
export const GATE_LAPSED = 'Not answered: the agent moved on when the next message came. Ask again if you still want this.';
/** approve = go ahead · revise = not like this, here is what to change, ask me again · deny = do not do it ·
 *  always = Hermes only: go ahead, and stop asking for this kind of action. */
export type GateChoice = 'approve' | 'revise' | 'deny' | 'always';
export type Decision = { id?: string; decision: GateChoice; note?: string; card_event?: string };

// The words a card's own buttons may use. Revise is tested first: "Send back" is not "Send".
const SAYS: [GateChoice, RegExp][] = [
  ['revise', /revis|edit|rework|change|feedback|redo|send back|re-?queue/i],
  ['deny', /deny|reject|decline|cancel|stop|^no\b/i],
  ['approve', /approve|accept|send|^(yes|ok)\b/i],
];
/** The command for a decision on a card: the card's own button for it if it has one, else `!approve <id>`. */
function commandFor(card: any, decision: GateChoice): string {
  const acts: any[] = Array.isArray(card.actions) ? card.actions.filter((a: any) => a && a.value) : [];
  const meant = (a: any) => String(a.role || a.kind || '').toLowerCase() || SAYS.find(([, re]) => re.test(String(a.label || '')))?.[0];
  const own = acts.find((a) => meant(a) === decision);
  return own ? String(own.value) : `!${decision}${card.id ? ` ${card.id}` : ''}`;
}
/** A decision on an approval card: a reply to the card, readable as a sentence (any Matrix client, any agent)
 *  and as data (`ai.getfxm.decision`), so the agent's skill does not have to parse text. */
async function sendDecision(roomId: string, ev: MEvent, decision: GateChoice, note: string): Promise<void> {
  const about = note.trim();
  const body = commandFor(ev.card, decision) + (about ? ` ${about}` : '');
  const data: Decision = { id: ev.card.id ? String(ev.card.id) : undefined, decision, note: about || undefined, card_event: ev.id };
  await sendText(roomId, body, { replyTo: ev.id, threadRoot: ev.threadRoot, extra: { 'ai.getfxm.decision': data } });
}

/** Answer a request. `note` is what to change (revise) or why not (deny). */
export async function answerGate(roomId: string, ev: MEvent, choice: GateChoice, note = ''): Promise<void> {
  answered.set(ev.id, choice); notify();
  try {
    if (isApproval(ev)) { await sendDecision(roomId, ev, choice === 'always' ? 'approve' : choice, note); return; }
    // A Hermes gate only knows yes and no: reuse the exact reaction the agent placed on it, else send the
    // text command. "Revise" is a no, followed by what to change as a reply to the gate.
    if (choice === 'always') { await sendText(roomId, '!approve always'); return; }
    const placed = Object.entries(getReactions(ev.id)).filter(([, who]) => who.includes(ev.sender)).map(([k]) => k);
    const want = choice === 'approve' ? APPROVE_KEYS : DENY_KEYS;
    const key = placed.find((k) => want.includes(norm(k)));
    if (key) await sendReaction(roomId, ev.id, key);
    else await sendText(roomId, choice === 'approve' ? '!approve' : '!deny');
    if (choice !== 'approve' && note.trim()) await sendText(roomId, note.trim(), { replyTo: ev.id, threadRoot: ev.threadRoot });
  } catch (e) {
    answered.delete(ev.id); notify();
    throw e;
  }
}

// ── Sync loop ──
const FILTER = JSON.stringify({
  room: {
    timeline: { limit: 30, lazy_load_members: true, unread_thread_notifications: true },
    state: { lazy_load_members: true },
    ephemeral: { types: ['m.typing', 'm.receipt'] },
  },
  presence: { types: [] },
});

let nextBatch: string | null = null;
let running = false;
let paused = false;
let abort: AbortController | null = null;
let onAuthLost: (() => void) | null = null;
export const setOnAuthLost = (cb: (() => void) | null) => { onAuthLost = cb; };
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

AppState.addEventListener('change', (s) => {
  if (s === 'active') { paused = false; }
  else { paused = true; abort?.abort(); saveCache(); } // drop the long-poll while backgrounded; flush the cache
});

let quickSync = false; // after restoring from cache, the first /sync returns immediately (timeout=0)
export function startSync() {
  if (running) return; running = true;
  loadCache().then((had) => { if (had) { quickSync = true; notify(); } loop(); });
}
export function stopSync() {
  running = false; abort?.abort(); syncState = 'idle';
  resetStore(); clearCache();
  notify();
}

async function loop() {
  let backoff = 1000;
  while (running && session) {
    while (paused && running) await sleep(500);
    if (!running) break;
    const initial = !nextBatch;
    if (initial && syncState !== 'initial') { syncState = 'initial'; notify(); }
    const ctrl = new AbortController(); abort = ctrl;
    try {
      const q = new URLSearchParams({ filter: FILTER, timeout: initial || quickSync ? '0' : '30000' });
      if (nextBatch) q.set('since', nextBatch);
      const res = await req(`/_matrix/client/v3/sync?${q.toString()}`, { signal: ctrl.signal });
      if (!running) break;
      quickSync = false;
      apply(res);
      nextBatch = res.next_batch;
      syncState = 'live'; backoff = 1000; notify();
    } catch (e: any) {
      if (!running) break;
      // We cancel the long-poll ourselves when the app goes to the background. Web reports that as
      // AbortError; Expo's native fetch on iOS as FetchRequestCanceledException — both are not errors.
      if (e?.name === 'AbortError' || ctrl.signal.aborted || /cancel/i.test(String(e?.message || ''))) continue;
      if (e?.status === 401) { onAuthLost?.(); break; }
      if (e?.status === 400 && nextBatch) {
        // Our saved sync position is no longer valid — start over from a full sync.
        console.log('[mx] sync token rejected, resyncing');
        resetStore(); clearCache(); quickSync = false; notify();
        continue;
      }
      console.log('[mx] sync error', e?.message);
      syncState = 'error'; notify();
      await sleep(backoff); backoff = Math.min(backoff * 2, 10000);
    }
  }
}

function newRoom(id: string): Room {
  return {
    id, name: 'Room', encrypted: false, isDm: false, members: {}, heroes: [], joined: 0, unread: 0,
    typing: [], reads: {}, threadUnread: {}, tags: {}, isSpace: false, children: [], parents: [], memberAvatars: {},
  };
}

function applyState(room: Room, ev: any) {
  switch (ev?.type) {
    case 'm.room.create': room.isSpace = ev.content?.type === 'm.space'; break;
    case 'm.room.name': room.nameState = ev.content?.name || ''; break;
    case 'm.room.topic': room.topic = ev.content?.topic; break;
    case 'm.room.canonical_alias': room.alias = ev.content?.alias; break;
    case 'm.room.encryption': room.encrypted = true; break;
    case 'm.room.join_rules': room.joinRule = ev.content?.join_rule; break;
    case 'm.room.power_levels': room.power = ev.content || {}; break;
    case 'm.room.avatar': room.avatarMxc = ev.content?.url || undefined; break;
    case 'm.room.member': {
      const uid = ev.state_key; if (!uid) break;
      const m = ev.content?.membership;
      if (m === 'join' || m === 'invite') {
        room.members[uid] = ev.content?.displayname || localpart(uid);
        if (ev.content?.avatar_url) room.memberAvatars[uid] = ev.content.avatar_url;
      } else if (m === 'leave' || m === 'ban') delete room.members[uid];
      break;
    }
    case 'm.space.child': {
      const child = ev.state_key; if (!child) break;
      const on = Array.isArray(ev.content?.via) && ev.content.via.length > 0;
      room.children = on ? [...new Set([...room.children, child])] : room.children.filter((c) => c !== child);
      break;
    }
    case 'm.space.parent': {
      const parent = ev.state_key; if (!parent) break;
      const on = Array.isArray(ev.content?.via) && ev.content.via.length > 0;
      room.parents = on ? [...new Set([...room.parents, parent])] : room.parents.filter((p) => p !== parent);
      break;
    }
    case 'ai.getfxm.agent': room.agentInfo = ev.content && typeof ev.content === 'object' ? ev.content : undefined; if (ev.sender) agentUsers.add(ev.sender); break;
  }
}

function applyEphemeral(room: Room, events: any[]) {
  const me = session?.userId;
  for (const ev of events) {
    if (ev?.type === 'm.typing') {
      room.typing = ((ev.content?.user_ids || []) as string[]).filter((u) => u !== me);
    } else if (ev?.type === 'm.receipt') {
      const reads = { ...room.reads };
      for (const [eventId, byType] of Object.entries<any>(ev.content || {})) {
        const rd = byType?.['m.read'] || {};
        for (const uid of Object.keys(rd)) if (uid !== me) reads[uid] = eventId;
      }
      room.reads = reads;
    }
  }
}

function resolveName(room: Room): string {
  if (room.nameState) return cleanName(room.nameState);
  if (room.alias) return room.alias;
  const me = session?.userId;
  const others = (room.heroes.length ? room.heroes : Object.keys(room.members)).filter((u) => u !== me && !isServiceUser(u));
  const names = others.map((u) => room.members[u] || localpart(u));
  if (names.length) return names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
  return room.isSpace ? 'Workspace' : 'Empty room';
}

function toEvent(roomId: string, e: any): MEvent | null {
  if (!e?.event_id || e.unsigned?.redacted_because) return null;
  const base = { id: e.event_id, roomId, sender: e.sender, ts: e.origin_server_ts || Date.now() };
  if (e.type === 'm.room.encrypted') return { ...base, kind: 'encrypted', body: 'Encrypted message — not supported yet' };
  if (e.type === 'm.room.member') {
    // Only real joins/leaves become visible lines (so a new room shows "parifma joined").
    const m = e.content?.membership; const prev = e.unsigned?.prev_content?.membership;
    const who = e.content?.displayname || e.unsigned?.prev_content?.displayname || localpart(e.state_key || e.sender);
    if (m === 'join' && prev !== 'join') return { ...base, kind: 'system', body: `${who} joined` };
    if (m === 'leave' && prev === 'join') return { ...base, kind: 'system', body: `${who} left` };
    return null;
  }
  if (e.type !== 'm.room.message') return null;
  const c = e.content || {};
  const html = c.format === 'org.matrix.custom.html' && typeof c.formatted_body === 'string' ? c.formatted_body : undefined;
  const rel = c['m.relates_to'];
  const threadRoot: string | undefined = rel?.rel_type === 'm.thread' ? rel.event_id : undefined;
  // In a thread, the "reply to" is only meaningful when it isn't the automatic fallback.
  const replyTo: string | undefined = threadRoot && rel?.is_falling_back_to_thread ? undefined : rel?.['m.in_reply_to']?.event_id;
  const th = e.unsigned?.['m.relations']?.['m.thread'];
  const { card, body: cleaned } = parseCard(c, replyTo ? stripReplyFallback(c.body ?? '') : (c.body ?? ''));
  // Only a program attaches a card as data, so whoever sent one is an agent (its avatar and badge follow).
  if ((c['ai.getfxm.card'] || c['ai.getfxm.envelope']) && e.sender && e.sender !== session?.userId) agentUsers.add(e.sender);
  const tm = c['ai.getfxm.thread'];
  const thread = tm && typeof tm === 'object' && typeof tm.kind === 'string' ? { kind: tm.kind, title: typeof tm.title === 'string' ? tm.title : undefined } : undefined;
  const dn = c['ai.getfxm.decision'];
  const decision: Decision | undefined = dn && typeof dn === 'object' && typeof dn.decision === 'string' ? dn : undefined;
  const extra = { replyTo, threadRoot, threadCount: th?.count, threadLatestTs: th?.latest_event?.origin_server_ts, threadLatestBy: th?.latest_event?.sender, card, thread, decision };
  switch (c.msgtype) {
    case 'm.image': return { ...base, ...extra, kind: 'image', body: c.body || 'Image', mxc: c.url, info: c.info };
    case 'm.file': case 'm.audio': case 'm.video': return { ...base, ...extra, kind: 'file', body: c.body || 'File', mxc: c.url, info: c.info };
    case 'm.emote': return { ...base, ...extra, kind: 'text', body: `* ${cleaned}`, html: html ? '* ' + html : undefined };
    default: return { ...base, ...extra, kind: 'text', body: cleaned, html: card ? undefined : html };
  }
}
/**
 * "Helm card": structured data a bot attaches to a message so the app can render it as a card
 * while humans still get the plain sentence. Two forms: content key `ai.getfxm.card`, or a
 * ```json fenced block in the body whose object has a `type`. The fenced block is removed from the text.
 */
// Atlas/Ann/Prometheus hand-off envelopes (content key ai.getfxm.envelope, Arjun's atlas_core wire format) are shown as
// cards: kind as the title, the human sentence as subtitle, build / from→to / mode and the flat payload as fields.
const ENV_RESERVED = new Set(['type', 'from', 'to', 'body', 'id', 'at', 'build_id', 's3_ref', 'mode', 'reply_to']);
const ENV_TITLE: Record<string, string> = {
  build_spec: 'Build spec', candidate_ready: 'Candidate ready', verdict: 'Verdict', deployed: 'Deployed', incident: 'Incident',
  fix_request: 'Fix request', escalation: 'Needs a decision', notification: 'Notification', ready_signal: 'Ready',
  credential_request: 'Credentials needed', credentials: 'Credentials', approval: 'Approval', spec_conflict: 'Spec conflict', context_mention: 'Mentioned in Context',
};
const ENV_CARD_TYPE: Record<string, string> = { escalation: 'approval', credential_request: 'approval', verdict: 'report', deployed: 'status', ready_signal: 'status', incident: 'handoff', build_spec: 'handoff', candidate_ready: 'handoff', fix_request: 'handoff', spec_conflict: 'approval' };
export function envelopeToCard(env: any): any {
  const kind = String(env.type || 'notification');
  const status = kind === 'verdict' ? String(env.verdict || env.result || env.status || '').toLowerCase() || undefined
    : kind === 'deployed' || kind === 'ready_signal' ? 'done'
    : kind === 'escalation' || kind === 'credential_request' || kind === 'spec_conflict' ? 'pending' : env.status;
  const fields: Record<string, string> = {};
  if (env.build_id) fields['Build'] = String(env.build_id);
  if (env.from || env.to) fields['From → to'] = `${env.from || '?'} → ${env.to || '?'}`;
  if (env.mode) fields['Mode'] = String(env.mode);
  for (const [k, v] of Object.entries(env)) {
    if (ENV_RESERVED.has(k) || v == null) continue;
    if (Array.isArray(v)) fields[k] = v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
    else if (typeof v !== 'object') fields[k] = String(v);
  }
  if (env.s3_ref) fields['S3'] = String(env.s3_ref);
  return { type: ENV_CARD_TYPE[kind] || 'info', title: ENV_TITLE[kind] || kind.replace(/_/g, ' ').replace(/^./, (ch) => ch.toUpperCase()), subtitle: env.body || undefined, status, fields, envelope: env };
}
function parseCard(c: any, body: string): { card?: any; body: string } {
  const direct = c['ai.getfxm.card'];
  const env = c['ai.getfxm.envelope'];
  if (!direct && env && typeof env === 'object') return { card: envelopeToCard(env), body };
  if (direct && typeof direct === 'object' && direct.type) return { card: direct, body };
  // A fenced block — the closing fence is optional, because a pasted card often loses its last line —
  // or, failing that, a bare {...} object (which then also needs a title, so ordinary JSON isn't hijacked).
  const fenced = /```(?:json)?\s*([\s\S]*?)(?:```|$)/i.exec(body);
  const m = fenced || /(\{[\s\S]*\})/.exec(body);
  if (m) {
    const raw = m[1]; const start = raw.indexOf('{'); const end = raw.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        const obj = JSON.parse(raw.slice(start, end + 1));
        const card = obj?.card && typeof obj.card === 'object' ? obj.card : obj;
        const ok = card && typeof card === 'object' && typeof card.type === 'string' && (fenced || typeof card.title === 'string');
        if (ok) return { card, body: body.replace(m[0], '').trim() };
      } catch { /* not a card */ }
    }
  }
  return { body };
}
/** Older clients prefix replies with a quoted "> …" fallback; drop it (the app renders the quote itself). */
function stripReplyFallback(body: string): string {
  if (!body.startsWith('> ')) return body;
  const lines = body.split('\n'); let i = 0;
  while (i < lines.length && lines[i].startsWith('> ')) i++;
  if (i < lines.length && lines[i].trim() === '') i++;
  return lines.slice(i).join('\n');
}

function appendEvents(roomId: string, evs: MEvent[], mode: 'append' | 'prepend') {
  const s = seen.get(roomId) || new Set<string>(); seen.set(roomId, s);
  const fresh = evs.filter((e) => !s.has(e.id)); fresh.forEach((e) => s.add(e.id));
  if (!fresh.length) return;
  const cur = timelines.get(roomId) || [];
  const next = mode === 'append' ? [...cur, ...fresh] : [...fresh, ...cur];
  timelines.set(roomId, next.length > 600 ? next.slice(-600) : next);
}

/** An event we already hold (a promoted local echo) is replaced by the server's fully parsed version. */
function refreshEvents(roomId: string, evs: MEvent[]) {
  const cur = timelines.get(roomId); if (!cur?.length) return;
  const byId = new Map(evs.map((e) => [e.id, e] as const));
  if (!cur.some((e) => byId.has(e.id))) return;
  // Keep our own local file for a just-sent attachment: the sender sees their copy, no server round trip needed.
  timelines.set(roomId, cur.map((e) => { const n = byId.get(e.id); return n ? (e.localUri && !n.localUri ? { ...n, localUri: e.localUri } : n) : e; }));
}

const lastMessage = (t: MEvent[] | undefined) => (t ? [...t].reverse().find((e) => e.kind !== 'system') : undefined);
function touchLast(roomId: string) {
  const room = rooms.get(roomId); const t = timelines.get(roomId);
  if (room && t?.length) rooms.set(roomId, { ...room, last: lastMessage(t) || room.last });
}

function applyInvites(inv: Record<string, any>) {
  const me = session?.userId;
  for (const [roomId, data] of Object.entries<any>(inv)) {
    const evs: any[] = data.invite_state?.events || [];
    let name = ''; let inviter = ''; let isDm = false; let isSpace = false; const names: Record<string, string> = {};
    for (const ev of evs) {
      if (ev.type === 'm.room.name') name = ev.content?.name || '';
      else if (ev.type === 'm.room.create') isSpace = ev.content?.type === 'm.space';
      else if (ev.type === 'm.room.member') {
        if (ev.content?.displayname) names[ev.state_key] = ev.content.displayname;
        if (ev.state_key === me && ev.content?.membership === 'invite') { inviter = ev.sender; isDm = !!ev.content?.is_direct; }
      }
    }
    const inviterName = names[inviter] || (inviter ? localpart(inviter) : 'Someone');
    invites.set(roomId, { id: roomId, name: name || (isDm ? inviterName : isSpace ? 'Workspace' : 'Room'), inviter, inviterName, isDm, isSpace });
  }
}

function apply(res: any) {
  for (const ev of res.account_data?.events || []) {
    if (ev.type === 'm.direct') {
      directMap = ev.content || {};
      directRooms = new Set((Object.values(directMap) as string[][]).flat());
    }
  }
  applyInvites(res.rooms?.invite || {});
  const join = res.rooms?.join || {};
  for (const [roomId, data] of Object.entries<any>(join)) {
    invites.delete(roomId);
    const room: Room = { ...(rooms.get(roomId) || newRoom(roomId)) };
    room.members = { ...room.members }; room.memberAvatars = { ...room.memberAvatars };
    const rawTimeline: any[] = data.timeline?.events || [];
    for (const ev of [...(data.state?.events || []), ...rawTimeline]) applyState(room, ev);
    for (const ev of rawTimeline) {
      recordReaction(ev);
      if (ev?.type === 'm.room.redaction' && ev.redacts) applyRedaction(roomId, ev.redacts);
      // Fallback: agent info sent as a plain timeline event (when it can't write room state).
      if (ev?.type === 'ai.getfxm.agent' && ev.state_key === undefined && ev.content && typeof ev.content === 'object') { room.agentInfo = ev.content; if (ev.sender) agentUsers.add(ev.sender); }
    }
    applyEphemeral(room, data.ephemeral?.events || []);
    for (const ev of data.account_data?.events || []) if (ev.type === 'm.tag') room.tags = ev.content?.tags || {};
    const sum = data.summary;
    if (sum) {
      if (sum['m.heroes']) room.heroes = sum['m.heroes'];
      if (sum['m.joined_member_count'] != null) room.joined = sum['m.joined_member_count'];
    }
    if (data.unread_notifications?.notification_count != null) room.unread = data.unread_notifications.notification_count;
    if (data.unread_thread_notifications && typeof data.unread_thread_notifications === 'object') {
      const m: Record<string, number> = {};
      for (const [root, c] of Object.entries<any>(data.unread_thread_notifications)) if (c?.notification_count) m[root] = c.notification_count;
      room.threadUnread = m;
    }
    room.isDm = room.isDm || directRooms.has(roomId);
    if (data.timeline) {
      if (!timelines.has(roomId)) room.prevBatch = data.timeline.prev_batch; // pagination token for "load earlier"
      const evs = rawTimeline.map((e: any) => toEvent(roomId, e)).filter(Boolean) as MEvent[];
      refreshEvents(roomId, evs);
      appendEvents(roomId, evs, 'append');
    }
    room.name = resolveName(room);
    const lm = lastMessage(timelines.get(roomId)); if (lm) room.last = lm;
    rooms.set(roomId, room);
  }
  for (const roomId of Object.keys(res.rooms?.leave || {})) {
    rooms.delete(roomId); invites.delete(roomId); timelines.delete(roomId); seen.delete(roomId);
  }
  for (const [id, r] of rooms) if (directRooms.has(id) && !r.isDm) rooms.set(id, { ...r, isDm: true });
}

// ── Messaging ──
export type Mention = { id: string; name: string };
const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export async function sendText(roomId: string, body: string, opts: { replyTo?: string; mentions?: Mention[]; threadRoot?: string; extra?: Record<string, any>; notice?: boolean } = {}): Promise<string> {
  if (!session) throw new Error('Not signed in');
  const txn = txnId('m');
  const content: any = { msgtype: opts.notice ? 'm.notice' : 'm.text', body, ...(opts.extra || {}) };
  if (opts.threadRoot) {
    // Matrix thread reply: relate to the root, with a reply-fallback to the latest message in the thread.
    const last = threadReplies(roomId, opts.threadRoot).slice(-1)[0]?.id || opts.threadRoot;
    content['m.relates_to'] = { rel_type: 'm.thread', event_id: opts.threadRoot, is_falling_back_to_thread: true, 'm.in_reply_to': { event_id: opts.replyTo || last } };
  } else if (opts.replyTo) content['m.relates_to'] = { 'm.in_reply_to': { event_id: opts.replyTo } };
  // Mentions: intentional-mentions metadata (m.mentions) + matrix.to pills in the HTML body,
  // so people/agents get notified and Hermes knows which bot is being addressed.
  const used = (opts.mentions || []).filter((m) => body.includes(`@${m.name}`));
  if (used.length) {
    content['m.mentions'] = { user_ids: [...new Set(used.map((m) => m.id))] };
    let html = escHtml(body);
    for (const m of used) html = html.split(escHtml(`@${m.name}`)).join(`<a href="https://matrix.to/#/${encodeURIComponent(m.id)}">${escHtml(m.name)}</a>`);
    content.format = 'org.matrix.custom.html'; content.formatted_body = html.replace(/\n/g, '<br>');
  }
  // The local echo carries the same rich body, so our own mentions render as pills immediately.
  const parsed = parseCard(content, body); // a ```json card typed by hand renders as a card right away
  const echo: MEvent = { id: `~${txn}`, roomId, sender: session.userId, ts: Date.now(), kind: 'text', body: parsed.body, card: parsed.card, html: parsed.card ? undefined : content.formatted_body, pending: true, replyTo: opts.replyTo, threadRoot: opts.threadRoot, thread: opts.extra?.['ai.getfxm.thread'], decision: opts.extra?.['ai.getfxm.decision'] };
  timelines.set(roomId, [...(timelines.get(roomId) || []), echo]); touchLast(roomId); notify();
  try {
    const r = await req(`${roomPath(roomId)}/send/m.room.message/${txn}`, { method: 'PUT', body: JSON.stringify(content) });
    const s = seen.get(roomId) || new Set<string>(); seen.set(roomId, s);
    let tl = timelines.get(roomId) || [];
    // If /sync already delivered the real event, drop the echo; otherwise promote it.
    if (s.has(r.event_id)) tl = tl.filter((e) => e.id !== echo.id);
    else { s.add(r.event_id); tl = tl.map((e) => (e.id === echo.id ? { ...e, id: r.event_id, pending: false } : e)); }
    timelines.set(roomId, tl); touchLast(roomId); notify();
    return r.event_id as string;
  } catch (e) {
    // Keep it in the thread, marked failed, so the user can retry instead of losing it.
    timelines.set(roomId, (timelines.get(roomId) || []).map((x) => (x.id === echo.id ? { ...x, pending: false, failed: true } : x))); notify();
    throw e;
  }
}
/** Re-send a failed message (removes the failed copy, sends fresh). */
export async function retrySend(roomId: string, eventId: string): Promise<void> {
  const tl = timelines.get(roomId) || [];
  const ev = tl.find((e) => e.id === eventId && e.failed);
  if (!ev) return;
  timelines.set(roomId, tl.filter((e) => e.id !== eventId)); notify();
  await sendText(roomId, ev.body);
}

export async function sendReaction(roomId: string, eventId: string, key: string): Promise<void> {
  if (!session) throw new Error('Not signed in');
  const content = { 'm.relates_to': { rel_type: 'm.annotation', event_id: eventId, key } };
  const r = await req(`${roomPath(roomId)}/send/m.reaction/${txnId('r')}`, { method: 'PUT', body: JSON.stringify(content) });
  recordReaction({ type: 'm.reaction', event_id: r.event_id, sender: session.userId, content });
  notify();
}
/** May I delete this message? My own always; someone else's only with the room's redact power (admins/moderators). */
export function canDelete(room: Room | undefined, ev: MEvent): boolean {
  if (!room || !session || ev.pending || ev.failed || ev.id.startsWith('~') || ev.kind === 'system') return false;
  if (ev.sender === session.userId) return true;
  const p = room.power || {};
  return (p.users?.[session.userId] ?? p.users_default ?? 0) >= (p.redact ?? 50);
}
/** Delete a message for everyone (Matrix redaction). The content is removed on the server; clients drop the message. */
export async function deleteMessage(roomId: string, eventId: string): Promise<void> {
  await req(`${roomPath(roomId)}/redact/${enc(eventId)}/${txnId('d')}`, { method: 'PUT', body: '{}' });
  applyRedaction(roomId, eventId); touchLast(roomId); notify();
}
/** Take back my own reaction (a Matrix redaction of the reaction event). */
export async function removeReaction(roomId: string, eventId: string, key: string): Promise<void> {
  const rid = myReactionId(eventId, key); if (!rid) return;
  await req(`${roomPath(roomId)}/redact/${enc(rid)}/${txnId('x')}`, { method: 'PUT', body: '{}' });
  applyRedaction(roomId, rid); notify();
}

/** Typing notification for the current user (server auto-expires after `timeout`). */
export function sendTyping(roomId: string, typing: boolean) {
  if (!session) return;
  req(`${roomPath(roomId)}/typing/${enc(session.userId)}`, {
    method: 'PUT', body: JSON.stringify(typing ? { typing: true, timeout: 20000 } : { typing: false }),
  }).catch(() => {});
}

// ── Media / attachments ──
export type Upload = { mxc: string; name: string; mime: string; size?: number; w?: number; h?: number };
/** Upload a local file (file:// or content:// uri) to the homeserver's media repo. */
export async function uploadMedia(uri: string, name: string, mime: string): Promise<Upload> {
  if (!session) throw new Error('Not signed in');
  if (Platform.OS !== 'web') {
    // Let the native side read file:// / content:// and stream the bytes; RN's fetch cannot open local files on Android.
    const res = await FileSystem.uploadAsync(`${MATRIX_HS}/_matrix/media/v3/upload?filename=${enc(name)}`, uri, {
      httpMethod: 'POST', uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
      headers: { 'Content-Type': mime || 'application/octet-stream', Authorization: `Bearer ${session.accessToken}` },
    });
    let data: any = {}; try { data = JSON.parse(res.body || '{}'); } catch {}
    if (res.status < 200 || res.status >= 300 || !data.content_uri) throw new MatrixError(data.error || `Upload failed (${res.status})`, res.status, data.errcode);
    const stat: any = await FileSystem.getInfoAsync(uri).catch(() => null);
    return { mxc: data.content_uri, name, mime, size: stat?.exists ? stat.size : undefined };
  }
  const blob = await (await fetch(uri)).blob();
  const resp = await fetch(`${MATRIX_HS}/_matrix/media/v3/upload?filename=${enc(name)}`, {
    method: 'POST',
    headers: { 'Content-Type': mime || 'application/octet-stream', Authorization: `Bearer ${session.accessToken}` },
    body: blob,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new MatrixError(data.error || `Upload failed (${resp.status})`, resp.status, data.errcode);
  return { mxc: data.content_uri, name, mime, size: (blob as any).size };
}
export async function sendAttachment(roomId: string, up: Upload, localUri?: string): Promise<void> {
  if (!session) throw new Error('Not signed in');
  const isImg = up.mime.startsWith('image/');
  const info: any = { mimetype: up.mime, size: up.size };
  if (isImg && up.w && up.h) { info.w = up.w; info.h = up.h; }
  const txn = txnId('a');
  // Local echo: the sender sees their photo/file at once (from the local file), pending until the server confirms.
  const echo: MEvent = { id: `~${txn}`, roomId, sender: session.userId, ts: Date.now(), kind: isImg ? 'image' : 'file', body: up.name, mxc: up.mxc, info, localUri, pending: true };
  timelines.set(roomId, [...(timelines.get(roomId) || []), echo]); touchLast(roomId); notify();
  try {
    const r = await req(`${roomPath(roomId)}/send/m.room.message/${txn}`, {
      method: 'PUT', body: JSON.stringify({ msgtype: isImg ? 'm.image' : 'm.file', body: up.name, url: up.mxc, info }),
    });
    const s = seen.get(roomId) || new Set<string>(); seen.set(roomId, s);
    let tl = timelines.get(roomId) || [];
    if (s.has(r.event_id)) tl = tl.filter((e) => e.id !== echo.id);
    else { s.add(r.event_id); tl = tl.map((e) => (e.id === echo.id ? { ...e, id: r.event_id, pending: false } : e)); }
    timelines.set(roomId, tl); touchLast(roomId); notify();
  } catch (e) {
    timelines.set(roomId, (timelines.get(roomId) || []).filter((x) => x.id !== echo.id)); notify();
    throw e;
  }
}

// ── Rooms: invites, membership, tags, creation, spaces ──
export async function acceptInvite(roomId: string): Promise<void> {
  await req(`${roomPath(roomId)}/join`, { method: 'POST', body: '{}' });
  invites.delete(roomId); notify();
}
/** Accept a workspace invitation and, as in Slack, land in its rooms: every room of it that workspace members may
 *  join. A room that refuses is skipped. Returns how many rooms were joined. */
export async function joinWorkspace(spaceId: string): Promise<number> {
  await acceptInvite(spaceId);
  const kids = await fetchSpaceRooms(spaceId).catch(() => [] as SpaceRoom[]);
  let joined = 0;
  for (const k of kids) {
    if (k.joined || (k.joinRule !== 'restricted' && k.joinRule !== 'public')) continue;
    try { await joinRoom(k.id); joined++; } catch { /* not open to me after all */ }
  }
  return joined;
}
export async function declineInvite(roomId: string): Promise<void> {
  await req(`${roomPath(roomId)}/leave`, { method: 'POST', body: '{}' });
  invites.delete(roomId); notify();
}
export async function leaveRoom(roomId: string): Promise<void> {
  await req(`${roomPath(roomId)}/leave`, { method: 'POST', body: '{}' });
  await req(`${roomPath(roomId)}/forget`, { method: 'POST', body: '{}' }).catch(() => {});
  rooms.delete(roomId); timelines.delete(roomId); seen.delete(roomId); notify();
}
/** Hide (archive) or unhide a room via the standard m.lowpriority tag. */
export async function setHidden(roomId: string, hidden: boolean): Promise<void> {
  if (!session) throw new Error('Not signed in');
  const path = `/_matrix/client/v3/user/${enc(session.userId)}/rooms/${enc(roomId)}/tags/${enc(HIDDEN_TAG)}`;
  if (hidden) await req(path, { method: 'PUT', body: JSON.stringify({ order: 0.5 }) });
  else await req(path, { method: 'DELETE' });
  const r = rooms.get(roomId);
  if (r) { const tags = { ...r.tags }; if (hidden) tags[HIDDEN_TAG] = { order: 0.5 }; else delete tags[HIDDEN_TAG]; rooms.set(roomId, { ...r, tags }); notify(); }
}
/** Keep (unarchive) an idle room: the m.favourite tag exempts it from auto-archive. */
export async function setKept(roomId: string, kept: boolean): Promise<void> {
  if (!session) throw new Error('Not signed in');
  const path = `/_matrix/client/v3/user/${enc(session.userId)}/rooms/${enc(roomId)}/tags/${enc(KEEP_TAG)}`;
  if (kept) await req(path, { method: 'PUT', body: JSON.stringify({ order: 0.1 }) });
  else await req(path, { method: 'DELETE' });
  const r = rooms.get(roomId);
  if (r) { const tags = { ...r.tags }; if (kept) tags[KEEP_TAG] = { order: 0.1 }; else delete tags[KEEP_TAG]; rooms.set(roomId, { ...r, tags }); notify(); }
}
/** The server's invite policy (helm-infra/matrix/modules) answers a refused invite with a generic
 *  M_FORBIDDEN "Invites have been disabled on this server"; say what it actually means. */
export const INVITE_BLOCKED_MSG = 'You can only start a conversation with people who are already in one of your workspaces or rooms. Ask a workspace admin to add them first.';
function friendlyInviteError(e: any): any {
  if (e instanceof MatrixError && e.status === 403 && e.errcode === 'M_FORBIDDEN' && /invites? (have been|are) disabled/i.test(e.message)) {
    return new MatrixError(INVITE_BLOCKED_MSG, 403, 'M_FORBIDDEN', e.data);
  }
  return e;
}
/** Resolve with the first non-pending event in the room that passes `test` and is not older than `sinceTs`; null on timeout. */
export function waitForEvent(roomId: string, test: (e: MEvent) => boolean, sinceTs: number, timeoutMs = 25000): Promise<MEvent | null> {
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const done = (v: MEvent | null) => { listeners.delete(check); if (timer) clearTimeout(timer); resolve(v); };
    const check = () => { const hit = (timelines.get(roomId) || []).find((e) => e.ts >= sinceTs && !e.pending && test(e)); if (hit) done(hit); };
    timer = setTimeout(() => done(null), timeoutMs);
    listeners.add(check); check();
  });
}
/** Hermes answers `/model` with a numbered list ("1️⃣ `model-id` — Provider") and a "Current model:" line.
 *  Helm turns that into a picker so nobody has to type `/model 3`. */
export type ModelOption = { n: number; id: string; provider?: string };
export const isModelList = (e: MEvent) => e.kind === 'text' && /current model:/i.test(e.body) && /(^|\n)\s*1[\uFE0F\u20E3]*[.)]?\s+\S/u.test(e.body);
export function parseModelList(body: string): { current?: string; currentProvider?: string; options: ModelOption[] } | null {
  const options: ModelOption[] = []; let current: string | undefined; let currentProvider: string | undefined;
  for (const raw of body.split('\n')) {
    const line = raw.replace(/[`*_]/g, '').trim();
    const cm = /^current model:\s*(\S+)/i.exec(line); if (cm) { current = cm[1]; continue; }
    const pm = /^provider:\s*(\S+)/i.exec(line); if (pm) { currentProvider = pm[1]; continue; }
    const m = /^(\d{1,2})[\uFE0F\u20E3]*[.)]?\s+(\S+?)(?:\s+[—–-]+\s+(.+))?$/u.exec(line);
    if (m) options.push({ n: Number(m[1]), id: m[2], provider: m[3]?.trim() });
  }
  return options.length ? { current, currentProvider, options } : null;
}
/** Does an option's provider label ("Custom endpoint", "Together AI") name the agent's current provider key ("custom")? */
export function sameProvider(label: string | undefined, key: string | undefined): boolean {
  if (!label || !key) return false;
  const a = label.toLowerCase().replace(/[^a-z0-9]/g, ''), b = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  return a === b || a.includes(b) || b.includes(a);
}
/** A number reaction on the model list ("1️⃣") = choose that model. Returns the number or null. */
export function modelChoiceFromKey(key: string): number | null { const m = /^(\d{1,2})[\uFE0F\u20E3]*$/u.exec(key.trim()); return m ? Number(m[1]) : null; }

/** Rename a room or workspace (m.room.name state; needs the room's state power level, usually admin). */
export async function setRoomName(roomId: string, name: string): Promise<void> {
  const clean = name.trim();
  if (!clean) throw new Error('Give it a name first.');
  await req(`${roomPath(roomId)}/state/m.room.name`, { method: 'PUT', body: JSON.stringify({ name: clean }) });
  const r = rooms.get(roomId);
  if (r) { const next = { ...r, nameState: clean }; rooms.set(roomId, { ...next, name: resolveName(next) }); notify(); }
}
export type DirectoryUser = { id: string; name: string; avatarMxc?: string };
/** People and agents the server lets this user find: by default, anyone they share a room with. */
export async function searchUsers(term: string, limit = 10): Promise<DirectoryUser[]> {
  const r = await req('/_matrix/client/v3/user_directory/search', { method: 'POST', body: JSON.stringify({ search_term: term, limit }) });
  return (r.results || []).map((u: any) => ({ id: u.user_id, name: u.display_name || localpart(u.user_id), avatarMxc: u.avatar_url || undefined }));
}
/** An exact Matrix id, whether or not it is in the directory; null when no such account exists. */
export async function lookupUser(mxid: string): Promise<DirectoryUser | null> {
  try { const r = await req(`/_matrix/client/v3/profile/${enc(mxid)}`); return { id: mxid, name: r.displayname || localpart(mxid), avatarMxc: r.avatar_url || undefined }; }
  catch (e: any) { if (e instanceof MatrixError && (e.status === 404 || e.status === 403)) return null; throw e; }
}
export const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
export type EmailInvite = { id: string; email: string; invited_by: string; at: number; status: 'pending' | 'invited' | 'joined' };
async function platformReq(path: string, opts: RequestInit = {}): Promise<any> {
  if (!session) throw new Error('Not signed in');
  const resp = await fetch(INVITE_API + path, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.accessToken}`, ...((opts.headers as any) || {}) } });
  const text = await resp.text(); let data: any = {}; try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON */ }
  if (!resp.ok) throw new MatrixError(data.message || data.error || `${resp.status} ${resp.statusText}`, resp.status, data.errcode || data.error, data);
  return data;
}
const PLATFORM_OFF = 'Email invites are not switched on for this server yet.';
/** The platform is not there at all: no such address, nothing answering, or a gateway with nothing behind it. */
function platformOff(e: any): boolean {
  if (e instanceof MatrixError) return e.status === 404 || e.status === 502 || e.status === 503;
  return e?.name === 'TypeError' || /failed to fetch|network request failed/i.test(String(e?.message || ''));
}
function friendlyPlatformError(e: any): any {
  if (platformOff(e)) return new Error(PLATFORM_OFF);
  if (e instanceof MatrixError) {
    if (e.status === 403) return new Error('You cannot invite people to this room.');
    if (e.status === 409) return new Error('They are already in this room.');
    return new Error(e.message);
  }
  return e;
}
/** Invite by email. The platform mails a sign-up link and its bot adds them to the room on first login
 *  (helm-infra docs/email-invites.md); the bot must already be in the room, so it is invited first if missing. */
export async function inviteByEmail(roomId: string, email: string): Promise<{ status: 'invited' | 'pending'; user_id?: string }> {
  const e = email.trim().toLowerCase();
  if (!isEmail(e)) throw new Error('That does not look like an email address.');
  // The platform first. While email invites are switched off, no bot gets pulled into the room for nothing:
  // a member cannot kick it out again. Any other answer is left to the real call below.
  await platformReq(`/invites?room_id=${enc(roomId)}`).catch((err) => { if (platformOff(err)) throw new Error(PLATFORM_OFF); });
  const room = rooms.get(roomId);
  if (room && !room.members[INVITE_BOT]) await inviteUser(roomId, INVITE_BOT).catch(() => { /* already invited, or no bot yet: the platform says so */ });
  const r = await platformReq('/invite-by-email', { method: 'POST', body: JSON.stringify({ room_id: roomId, email: e }) }).catch((err) => { throw friendlyPlatformError(err); });
  return { status: r.status === 'invited' ? 'invited' : 'pending', user_id: r.user_id };
}
/** People invited by email who have not signed up yet (empty when the platform side is not there). */
export async function listEmailInvites(roomId: string): Promise<EmailInvite[]> {
  const r = await platformReq(`/invites?room_id=${enc(roomId)}`).catch(() => ({ invites: [] }));
  return ((r.invites || []) as EmailInvite[]).filter((i) => i.status === 'pending');
}
export async function inviteUser(roomId: string, user: string): Promise<string> {
  const mxid = toMxid(user);
  await req(`${roomPath(roomId)}/invite`, { method: 'POST', body: JSON.stringify({ user_id: mxid }) }).catch((e) => { throw friendlyInviteError(e); });
  return mxid;
}
const alreadyIn = (e: any) => e instanceof MatrixError && e.status === 403 && /already/i.test(e.message);
/** Invite someone into a room the Slack way: a person new to the room's workspace is invited into the workspace
 *  too, so they see what everyone in it sees. When only admins may invite to the workspace and I am not one, the
 *  whole invite is refused before anything is sent: the wall is the point. Returns the workspaces they were
 *  invited into. */
export async function inviteToRoom(roomId: string, user: string): Promise<{ mxid: string; workspaces: Room[] }> {
  const mxid = toMxid(user);
  const need: Room[] = [];
  for (const sp of workspacesOf(roomId)) {
    if (!sp.membersLoaded) await loadMembers(sp.id).catch(() => {});
    const cur = rooms.get(sp.id) || sp;
    if (cur.members[mxid]) continue;
    if (!canInvite(cur)) {
      const admin = roomAdmin(cur);
      throw new Error(`Only admins can bring new people into "${cur.name}". Ask ${admin ? senderName(cur, admin) : 'the workspace admin'} to invite them to the workspace first.`);
    }
    need.push(cur);
  }
  await inviteUser(roomId, mxid);
  const workspaces: Room[] = [];
  for (const sp of need) {
    try { await inviteUser(sp.id, mxid); workspaces.push(sp); } catch (e) { if (!alreadyIn(e)) throw e; }
  }
  return { mxid, workspaces };
}
/** Remove someone from a room or workspace (Matrix kick). */
export async function kickUser(roomId: string, uid: string, reason?: string): Promise<void> {
  await req(`${roomPath(roomId)}/kick`, { method: 'POST', body: JSON.stringify(reason ? { user_id: uid, reason } : { user_id: uid }) });
  const room = rooms.get(roomId);
  if (room?.members[uid]) { const members = { ...room.members }; delete members[uid]; rooms.set(roomId, { ...room, members }); notify(); }
}
/** Remove someone from a workspace and from every room of it I may remove them from, in one go, as Slack does.
 *  Rooms where I lack the power are left alone and named, so the admin knows. */
export async function removeFromWorkspace(spaceId: string, uid: string): Promise<{ removed: string[]; kept: string[] }> {
  const sp = rooms.get(spaceId); if (!sp) throw new Error('Workspace not found');
  const removed: string[] = []; const kept: string[] = [];
  for (const id of sp.children) {
    const r = rooms.get(id); if (!r || r.isSpace) continue;
    if (!r.membersLoaded) await loadMembers(id).catch(() => {});
    const cur = rooms.get(id) || r;
    if (!cur.members[uid]) continue;
    if (!canKick(cur)) { kept.push(cur.name); continue; }
    try { await kickUser(id, uid, `Removed from ${sp.name}`); removed.push(cur.name); } catch { kept.push(cur.name); }
  }
  await kickUser(spaceId, uid, 'Removed from the workspace');
  return { removed, kept };
}
/** Slack's "only admins can invite people": the workspace's invite power level. The other levels go back as they
 *  are; while they are not known, nothing is sent (a power-levels event without its users would strip everyone). */
export async function setAdminsOnlyInvite(spaceId: string, on: boolean): Promise<void> {
  const room = rooms.get(spaceId);
  if (!room?.power?.users) throw new Error('This workspace’s permissions are not loaded yet. Try again in a moment.');
  const content = { ...room.power, invite: on ? ADMIN_LEVEL : 0 };
  await req(`${roomPath(spaceId)}/state/m.room.power_levels`, { method: 'PUT', body: JSON.stringify(content) });
  rooms.set(spaceId, { ...room, power: content }); notify();
}
/** Full joined-member list (sync only lazy-loads senders). */
export async function loadMembers(roomId: string): Promise<void> {
  const r = await req(`${roomPath(roomId)}/members?membership=join`);
  const room = rooms.get(roomId); if (!room) return;
  const members = { ...room.members };
  for (const ev of r.chunk || []) if (ev.type === 'm.room.member' && ev.state_key) members[ev.state_key] = ev.content?.displayname || localpart(ev.state_key);
  rooms.set(roomId, { ...room, members, membersLoaded: true, name: resolveName({ ...room, members }) }); notify();
}
export async function createRoom(opts: { name?: string; invite?: string[]; isDirect?: boolean; topic?: string }): Promise<string> {
  const invite = (opts.invite || []).map(toMxid);
  const body: any = {
    preset: opts.isDirect ? 'trusted_private_chat' : 'private_chat', invite, is_direct: !!opts.isDirect,
    // Let any member (i.e. the agent) publish the `ai.getfxm.agent` info state event.
    power_level_content_override: { events: { 'ai.getfxm.agent': 0 } },
  };
  if (opts.name) body.name = opts.name;
  if (opts.topic) body.topic = opts.topic;
  const r = await req('/_matrix/client/v3/createRoom', { method: 'POST', body: JSON.stringify(body) }).catch((e) => { throw friendlyInviteError(e); });
  const roomId: string = r.room_id;
  if (opts.isDirect && invite[0] && session) {
    // Register it as a DM in account data (m.direct), best-effort.
    const next = { ...directMap, [invite[0]]: [...new Set([...(directMap[invite[0]] || []), roomId])] };
    req(`/_matrix/client/v3/user/${enc(session.userId)}/account_data/m.direct`, { method: 'PUT', body: JSON.stringify(next) }).catch(() => {});
  }
  return roomId;
}
/** A Space = a workspace: a room that groups other rooms. */
export async function createSpace(name: string, invite: string[] = []): Promise<string> {
  const r = await req('/_matrix/client/v3/createRoom', {
    method: 'POST',
    body: JSON.stringify({ name, preset: 'private_chat', creation_content: { type: 'm.space' }, invite: invite.map(toMxid) }),
  });
  return r.room_id;
}
/**
 * Room access. 'invite' = private (invite-only). 'restricted' = anyone who is a member of
 * one of the given workspaces may join on their own — the Element "space members can join" model.
 */
export async function setJoinRule(roomId: string, rule: 'invite' | 'restricted', spaceIds: string[] = []): Promise<void> {
  const content = rule === 'restricted'
    ? { join_rule: 'restricted', allow: spaceIds.map((id) => ({ type: 'm.room_membership', room_id: id })) }
    : { join_rule: 'invite' };
  await req(`${roomPath(roomId)}/state/m.room.join_rules`, { method: 'PUT', body: JSON.stringify(content) });
  const r = rooms.get(roomId);
  if (r) { rooms.set(roomId, { ...r, joinRule: rule }); notify(); }
}
/** Adding a room to a workspace also opens it to workspace members (best-effort; needs room admin). */
export async function addRoomToSpace(spaceId: string, roomId: string): Promise<void> {
  const via = [serverName()];
  await req(`${roomPath(spaceId)}/state/m.space.child/${enc(roomId)}`, { method: 'PUT', body: JSON.stringify({ via }) });
  await req(`${roomPath(roomId)}/state/m.space.parent/${enc(spaceId)}`, { method: 'PUT', body: JSON.stringify({ via, canonical: true }) }).catch(() => {});
  const sp = rooms.get(spaceId);
  if (sp) { rooms.set(spaceId, { ...sp, children: [...new Set([...sp.children, roomId])] }); notify(); }
  const r = rooms.get(roomId);
  const parents = [...new Set([...(r?.parents || []), spaceId])];
  await setJoinRule(roomId, 'restricted', parents).catch(() => {});
}
/** Removing a room from a workspace makes it invite-only again unless another workspace still holds it. */
export async function removeRoomFromSpace(spaceId: string, roomId: string): Promise<void> {
  await req(`${roomPath(spaceId)}/state/m.space.child/${enc(roomId)}`, { method: 'PUT', body: '{}' });
  await req(`${roomPath(roomId)}/state/m.space.parent/${enc(spaceId)}`, { method: 'PUT', body: '{}' }).catch(() => {});
  const sp = rooms.get(spaceId);
  if (sp) { rooms.set(spaceId, { ...sp, children: sp.children.filter((c) => c !== roomId) }); notify(); }
  const r = rooms.get(roomId);
  const parents = (r?.parents || []).filter((p) => p !== spaceId);
  await setJoinRule(roomId, parents.length ? 'restricted' : 'invite', parents).catch(() => {});
}

/** Rooms inside a workspace, including ones I haven't joined yet (Space hierarchy). */
export type SpaceRoom = { id: string; name: string; topic?: string; members: number; joinRule?: string; joined: boolean };
export async function fetchSpaceRooms(spaceId: string): Promise<SpaceRoom[]> {
  const r = await req(`/_matrix/client/v1/rooms/${enc(spaceId)}/hierarchy?limit=100&suggested_only=false`);
  return ((r.rooms || []) as any[])
    .filter((x) => x.room_id !== spaceId && x.room_type !== 'm.space')
    .map((x) => ({
      id: x.room_id, name: x.name || x.canonical_alias || 'Room', topic: x.topic, members: x.num_joined_members || 0,
      joinRule: x.join_rule, joined: rooms.has(x.room_id),
    }));
}
export async function joinRoom(roomId: string): Promise<void> {
  await req(`/_matrix/client/v3/join/${enc(roomId)}?server_name=${enc(serverName())}`, { method: 'POST', body: '{}' });
}

// ── Threads (native Matrix threads: replies related to a root message) ──
const threadFetched = new Map<string, MEvent[]>();   // `${roomId}|${rootId}` → replies fetched from the server
const threadRoots = new Map<string, MEvent[]>();     // roomId → thread root messages (from /threads)
const tkey = (roomId: string, rootId: string) => `${roomId}|${rootId}`;
/** Replies for a thread: fetched ones + any that arrived via /sync, deduped, oldest first. */
export function threadReplies(roomId: string, rootId: string): MEvent[] {
  const seenIds = new Set<string>(); const out: MEvent[] = [];
  for (const e of [...(threadFetched.get(tkey(roomId, rootId)) || []), ...(timelines.get(roomId) || [])]) {
    if (e.threadRoot !== rootId || seenIds.has(e.id)) continue;
    seenIds.add(e.id); out.push(e);
  }
  return out.sort((a, b) => a.ts - b.ts);
}
/** A thread "counts" only once a human has written in it. Hermes sends every answer as a thread reply
 *  rooted at the message it answers, which would otherwise turn each exchange into a thread. */
export function isRealThread(roomId: string, rootId: string): boolean {
  const root = (timelines.get(roomId) || []).find((e) => e.id === rootId) || (threadRoots.get(roomId) || []).find((e) => e.id === rootId);
  if (root?.thread) return true;                       // opened on purpose (Helm's "new conversation", an agent's build thread)
  if (root && isAgentUser(root.sender)) return true;   // an agent started it: that is the closed-loop pattern, not Hermes's auto-threading
  // A thread we only know from the server's list has no replies loaded yet. Its latest reply being a person's
  // is enough to know a person wrote in it; without this such a thread was not counted until it was opened.
  if (root?.threadLatestBy && !isAgentUser(root.threadLatestBy)) return true;
  return threadReplies(roomId, rootId).some((e) => !isAgentUser(e.sender));
}
/** What to call a thread in lists: its declared title, its card title, or the root's first line. */
export function threadTitle(root: MEvent): string {
  if (root.thread?.title) return root.thread.title;
  if (root.card?.title) return String(root.card.title);
  if (root.kind === 'image') return '📷 Photo'; if (root.kind === 'file') return `📎 ${root.body}`;
  return (root.body.split('\n').find((l) => l.trim()) || root.body).trim();
}
/** Open a thread on purpose: a notice root (agents ignore notices, so nobody answers the root itself)
 *  carrying ai.getfxm.thread so every client knows it is a deliberate thread with a title. */
export async function startThread(roomId: string, title: string, kind = 'conversation'): Promise<string> {
  return sendText(roomId, `New conversation · ${title}`, { notice: true, extra: { 'ai.getfxm.thread': { kind, title } } });
}
/** The room an agent "lives" in: its provisioned room, else a DM with it, else the smallest room it is in. */
export function agentHomeRoom(agentId: string): Room | undefined {
  const mine = [...rooms.values()].filter((r) => !r.isSpace && r.members[agentId] && !isHidden(r));
  const all = mine.length ? mine : [...rooms.values()].filter((r) => !r.isSpace && r.members[agentId]);
  return all.find((r) => /FxM Agent\s*$/i.test(r.nameState || '')) || all.find((r) => r.isDm)
    || all.sort((a, b) => Object.keys(a.members).length - Object.keys(b.members).length)[0];
}
export function useThread(roomId: string, rootId: string): { root?: MEvent; replies: MEvent[] } {
  const v = useSyncExternalStore(subscribe, () => version); void v;
  const root = (timelines.get(roomId) || []).find((e) => e.id === rootId) || (threadRoots.get(roomId) || []).find((e) => e.id === rootId);
  return { root, replies: threadReplies(roomId, rootId) };
}
export async function loadThread(roomId: string, rootId: string): Promise<void> {
  const q = new URLSearchParams({ limit: '100', dir: 'f' });
  const r = await req(`/_matrix/client/v1/rooms/${enc(roomId)}/relations/${enc(rootId)}/m.thread?${q.toString()}`);
  const evs = ((r.chunk || []).map((e: any) => toEvent(roomId, e)).filter(Boolean) as MEvent[]).map((e) => ({ ...e, threadRoot: rootId }));
  for (const ev of r.chunk || []) recordReaction(ev);
  threadFetched.set(tkey(roomId, rootId), evs); notify();
}
/** All threads in a room, most recently active first. */
export function useThreadRoots(roomId: string): MEvent[] {
  const v = useSyncExternalStore(subscribe, () => version); void v;
  const fromServer = threadRoots.get(roomId) || [];
  const fromTimeline = (timelines.get(roomId) || []).filter((e) => !!e.thread || (e.threadCount || 0) > 0 || (timelines.get(roomId) || []).some((x) => x.threadRoot === e.id));
  const seenIds = new Set<string>(); const out: MEvent[] = [];
  for (const e of [...fromTimeline, ...fromServer]) { if (!seenIds.has(e.id) && isRealThread(roomId, e.id)) { seenIds.add(e.id); out.push(e); } }
  const latest = (e: MEvent) => Math.max(e.threadLatestTs || 0, ...threadReplies(roomId, e.id).map((x) => x.ts), e.ts);
  return out.sort((a, b) => latest(b) - latest(a));
}
const threadRootsAt = new Map<string, number>();     // roomId → when its thread list was last asked for
/** Ask the server for a room's threads. The messages on screen only show the threads that started inside the
 *  loaded window, so a room has to ask once when it opens or its thread count appears late, or never.
 *  `freshFor`: skip the request when the list was fetched less than that many milliseconds ago. */
export async function loadThreadRoots(roomId: string, freshFor = 0): Promise<void> {
  const asked = threadRootsAt.get(roomId) || 0;
  if (freshFor && Date.now() - asked < freshFor) return;
  threadRootsAt.set(roomId, Date.now());
  try {
    const r = await req(`/_matrix/client/v1/rooms/${enc(roomId)}/threads?include=all&limit=50`);
    const roots = ((r.chunk || []).map((e: any) => toEvent(roomId, e)).filter(Boolean) as MEvent[]);
    threadRoots.set(roomId, roots); notify(); scheduleSave();
  } catch (e) {
    threadRootsAt.set(roomId, asked); // let the next open try again
    throw e;
  }
}

/** Fetch older history. Resolves false when there's nothing more to load. */
export async function loadOlder(roomId: string): Promise<boolean> {
  const room = rooms.get(roomId); if (!room?.prevBatch) return false;
  const q = new URLSearchParams({ dir: 'b', from: room.prevBatch, limit: '40', filter: JSON.stringify({ lazy_load_members: true }) });
  const r = await req(`${roomPath(roomId)}/messages?${q.toString()}`);
  const rr: Room = { ...room, members: { ...room.members } };
  for (const ev of r.state || []) applyState(rr, ev);
  for (const ev of r.chunk || []) { if (ev.type === 'm.room.member') applyState(rr, ev); recordReaction(ev); }
  rr.prevBatch = r.end || undefined;
  const evs = ((r.chunk || []).map((e: any) => toEvent(roomId, e)).filter(Boolean) as MEvent[]).reverse(); // dir=b → newest first
  appendEvents(roomId, evs, 'prepend');
  rr.name = resolveName(rr);
  rooms.set(roomId, rr); notify();
  return !!r.end && evs.length > 0;
}

/** Clear the unread badge locally and post a read receipt for the latest event. */
const threadReceiptSent = new Map<string, string>(); // `${room}|${root}` → last reply id we acknowledged
/** Opening a thread reads it: clear its count locally and send a threaded receipt for its latest reply. */
export function markThreadRead(roomId: string, rootId: string) {
  const room = rooms.get(roomId); if (!room) return;
  if (room.threadUnread[rootId]) { const next = { ...room.threadUnread }; delete next[rootId]; rooms.set(roomId, { ...room, threadUnread: next }); notify(); }
  const replies = threadReplies(roomId, rootId).filter((e) => !e.pending);
  const last = replies[replies.length - 1]; if (!last) return;
  const key = `${roomId}|${rootId}`; if (threadReceiptSent.get(key) === last.id) return; threadReceiptSent.set(key, last.id);
  req(`${roomPath(roomId)}/receipt/m.read/${enc(last.id)}`, { method: 'POST', body: JSON.stringify({ thread_id: rootId }) }).catch(() => {});
}
export const threadUnreadTotal = (room: Room | undefined): number => room ? Object.values(room.threadUnread || {}).reduce((a, b) => a + b, 0) : 0;
export function markRead(roomId: string) {
  const room = rooms.get(roomId);
  if (room && room.unread) { rooms.set(roomId, { ...room, unread: 0 }); notify(); }
  const tl = timelines.get(roomId);
  const last = tl ? [...tl].reverse().find((e) => !e.pending && !e.failed && !e.id.startsWith('~')) : undefined;
  if (!last) return;
  req(`${roomPath(roomId)}/receipt/m.read/${enc(last.id)}`, { method: 'POST', body: '{}' }).catch(() => {});
}

/** mxc:// → authenticated media URL (+ headers) for <Image source>. */
export function mediaSource(mxc: string | undefined, thumb?: { w: number; h: number }) {
  const m = /^mxc:\/\/([^/]+)\/(.+)$/.exec(mxc || ''); if (!m) return null;
  const uri = thumb
    ? `${MATRIX_HS}/_matrix/client/v1/media/thumbnail/${m[1]}/${m[2]}?width=${thumb.w}&height=${thumb.h}&method=scale`
    : `${MATRIX_HS}/_matrix/client/v1/media/download/${m[1]}/${m[2]}`;
  const headers: Record<string, string> = session ? { Authorization: `Bearer ${session.accessToken}` } : {};
  return { uri, headers };
}
